<script setup lang="ts">
import type { CalendarDateRangeValue } from '@fantastic-admin/components'
import type { DateValue } from '@internationalized/date'
import type { HTMLAttributes } from 'vue'
import { FaButton, FaCalendarDateRange, FaIcon } from '@fantastic-admin/components'
import { CalendarDate, getLocalTimeZone } from '@internationalized/date'

defineOptions({
  name: 'AppDateRangePicker',
})

const props = withDefaults(defineProps<{
  modelValue?: string[] | null
  type?: 'daterange'
  valueFormat?: string
  placeholder?: string
  startPlaceholder?: string
  endPlaceholder?: string
  rangeSeparator?: string
  clearable?: boolean
  disabled?: boolean
  readonly?: boolean
  size?: '' | 'large' | 'default' | 'small'
  class?: HTMLAttributes['class']
  disabledDate?: (date: Date) => boolean
}>(), {
  modelValue: () => [],
  type: 'daterange',
  valueFormat: 'YYYY-MM-DD',
  placeholder: '',
  startPlaceholder: '开始日期',
  endPlaceholder: '结束日期',
  rangeSeparator: '-',
  clearable: true,
  disabled: false,
  readonly: false,
  size: 'default',
  class: undefined,
  disabledDate: undefined,
})

const emit = defineEmits<{
  'update:modelValue': [value: string[]]
  'change': [value: string[]]
  'clear': []
}>()

const internalValue = shallowRef<CalendarDateRangeValue>(toRangeValue(props.modelValue))
const hasValue = computed(() => Boolean(internalValue.value.start || internalValue.value.end))
const triggerSize = computed(() => props.size === 'small' ? 'sm' : props.size === 'large' ? 'lg' : 'default')
const pickerPlaceholder = computed(() => props.placeholder || `${props.startPlaceholder} ${props.rangeSeparator} ${props.endPlaceholder}`)

watch(() => props.modelValue, (value) => {
  internalValue.value = toRangeValue(value)
}, { deep: true })

function parseDateValue(value: string | undefined) {
  if (!value) {
    return undefined
  }
  const matched = value.match(/^(\d{4})-(\d{2})-(\d{2})/)
  if (matched) {
    const [, year, month, day] = matched
    return new CalendarDate(Number(year), Number(month), Number(day))
  }

  const date = new Date(value)
  if (Number.isNaN(date.getTime())) {
    return undefined
  }
  return new CalendarDate(date.getFullYear(), date.getMonth() + 1, date.getDate())
}

function toRangeValue(value: string[] | null | undefined): CalendarDateRangeValue {
  return {
    start: parseDateValue(value?.[0]),
    end: parseDateValue(value?.[1]),
  }
}

function formatDateValue(value: DateValue) {
  const parts: Record<string, string> = {
    YYYY: String(value.year).padStart(4, '0'),
    MM: String(value.month).padStart(2, '0'),
    DD: String(value.day).padStart(2, '0'),
  }
  return props.valueFormat.replace(/YYYY|MM|DD/g, token => parts[token] ?? token)
}

function formatRangeValue(value: CalendarDateRangeValue) {
  if (!value.start && !value.end) {
    return pickerPlaceholder.value
  }
  return [value.start ? formatDateValue(value.start) : props.startPlaceholder, value.end ? formatDateValue(value.end) : props.endPlaceholder]
    .join(` ${props.rangeSeparator} `)
}

function handleUpdate(value: CalendarDateRangeValue) {
  internalValue.value = value
  if (value.start && value.end) {
    emit('update:modelValue', [formatDateValue(value.start), formatDateValue(value.end)])
  }
}

function handleComplete(value: { start: DateValue, end: DateValue }) {
  const formattedValue = [formatDateValue(value.start), formatDateValue(value.end)]
  emit('change', formattedValue)
}

function handleClear() {
  if (props.disabled || props.readonly) {
    return
  }
  internalValue.value = { start: undefined, end: undefined }
  emit('update:modelValue', [])
  emit('clear')
}

function isDateDisabled(value: DateValue) {
  return props.disabledDate?.(value.toDate(getLocalTimeZone())) ?? false
}
</script>

<template>
  <FaCalendarDateRange
    :model-value="internalValue"
    picker
    :picker-placeholder="pickerPlaceholder"
    :disabled="props.disabled"
    :readonly="props.readonly"
    :is-date-disabled="props.disabledDate ? isDateDisabled : undefined"
    :format-value="formatRangeValue"
    @update:model-value="handleUpdate"
    @complete="handleComplete"
  >
    <template #trigger="{ label }">
      <FaButton
        variant="outline"
        :size="triggerSize"
        :disabled="props.disabled"
        class="font-normal text-left w-full justify-start overflow-hidden" :class="[!hasValue && 'text-muted-foreground', props.class]"
      >
        <FaIcon name="i-lucide:calendar-range" class="flex-none size-4" />
        <span class="flex-1 min-w-0 truncate">{{ label }}</span>
        <span
          v-if="props.clearable && hasValue && !props.readonly"
          class="text-muted-foreground ml-auto rounded-sm inline-flex flex-none size-5 items-center justify-center hover:text-foreground"
          role="button"
          tabindex="0"
          aria-label="清空日期范围"
          @pointerdown.stop
          @click.stop.prevent="handleClear"
        >
          <FaIcon name="i-lucide:x" class="size-3.5" />
        </span>
      </FaButton>
    </template>
  </FaCalendarDateRange>
</template>
